import { Group } from '@visx/group';
import { Pie } from '@visx/shape';
import { arc, DefaultArcObject } from 'd3-shape';
import { forwardRef, Fragment, useMemo } from 'react';

import { container } from './PieChartPlot.sty.js';
import { useGraphSize } from '../../../core/components/chart-layout/hooks/useGraphSize.js';
import { useTooltipReducer } from '../../../core/components/overlay-tooltip/useTooltipReducer.js';
import { CHART_VARIANT, PAD_ANGLE } from '../../constants.js';
import {
  useFormatter,
  useTruncationMode,
} from '../../context/FormatterContext.js';
import { useLabels } from '../../context/LabelsContext.js';
import {
  useHighlightedIdState,
  useVisibleSlicesState,
} from '../../hooks/store/selectors.js';
import {
  type ChartVariant,
  type InternalLabel,
  InternalSliceData,
} from '../../types/internal/pie-chart.js';
import { PieChartProps } from '../../types/pie-chart.config.js';
import { buildChartSizes } from '../../utils/build-chart-sizes.js';
import { buildLabel } from '../../utils/build-label.js';
import { collision, isInBounds } from '../../utils/label-collisions.js';
import { InnerWrapper } from '../InnerWrapper/InnerWrapper.js';
import { PieChartLabel } from '../PieChartLabel.js';
import { PieChartSlice } from '../PieChartSlice.js';

type PieChartPlotProps = Omit<PieChartProps, 'data'> & {
  variant: ChartVariant;
};

const getValue = (pieData: InternalSliceData) => pieData.relativeValue;

export const PieChartPlot = forwardRef<HTMLDivElement, PieChartPlotProps>(
  (props, forwardedRef) => {
    const { variant } = props;
    const dispatchReducer = useTooltipReducer();
    const visibleSlices = useVisibleSlicesState();
    const highlightedId = useHighlightedIdState();
    const { height, width } = useGraphSize();
    const { hidden: hiddenLabels, valueType } = useLabels();
    const formatter = useFormatter();
    const truncationMode = useTruncationMode();
    const { topCenter, radius, leftCenter, innerRadius } = buildChartSizes(
      height,
      width,
      variant,
      visibleSlices,
      !!hiddenLabels,
    );

    const visibleIds = visibleSlices.map((slice) => slice.id);

    const donutOverlay = useMemo(
      () =>
        arc()
          .innerRadius(innerRadius)
          .outerRadius(radius)
          .startAngle(0)
          .endAngle(360)({} as DefaultArcObject),
      [innerRadius, radius],
    ) as string | undefined;

    return (
      <div className={container} ref={forwardedRef}>
        {variant === CHART_VARIANT.Donut && (
          <InnerWrapper
            width={width}
            height={height}
            innerRadius={innerRadius}
            visibleIds={visibleIds}
          />
        )}

        <svg width={width} height={height} style={{ overflow: 'visible' }}>
          <Group
            top={topCenter}
            left={leftCenter}
            onMouseLeave={dispatchReducer({ type: 'SET_OUT_BOUNDS' })}
          >
            {variant === CHART_VARIANT.Pie ? (
              <circle
                cx="0"
                cy="0"
                r={radius > 0 ? radius : 0}
                fill="transparent"
              />
            ) : (
              <path d={donutOverlay} fill="transparent" />
            )}

            <Pie
              data={visibleSlices}
              pieValue={getValue}
              outerRadius={radius}
              innerRadius={innerRadius}
              padAngle={PAD_ANGLE}
              pieSort={null}
            >
              {(pie) => {
                let previousLabel: InternalLabel | undefined = undefined;

                return pie.arcs.map((arc) => {
                  const label = buildLabel(
                    arc,
                    pie.path,
                    valueType,
                    formatter,
                    truncationMode,
                    leftCenter,
                  );

                  const isHighlighted =
                    !highlightedId || highlightedId === arc.data.id;

                  const showLabel =
                    !hiddenLabels &&
                    isInBounds(label, leftCenter, topCenter) &&
                    (!previousLabel || !collision(label, previousLabel)) &&
                    arc.data.id !== 'thinner-slices';

                  if (showLabel) {
                    previousLabel = label;
                  }

                  return (
                    <Fragment key={`slice-${arc.data.id}`}>
                      <PieChartSlice
                        sliceId={`slice-${arc.data.id}`}
                        arc={arc}
                        data={arc.data}
                        path={pie.path}
                        isHighlighted={isHighlighted}
                      />
                      {showLabel && (
                        <PieChartLabel
                          x={label.textX}
                          y={label.textY}
                          text={label.text}
                          isHighlighted={isHighlighted}
                        />
                      )}
                    </Fragment>
                  );
                });
              }}
            </Pie>
          </Group>
        </svg>
      </div>
    );
  },
);
